import Link from 'next/link';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Container from '~/components/Container';
import Icon from '~/components/Icon';
import SectionTitle from '~/components/SectionTitle';
import Text from '~/components/Text';
import { ProductListKey, RelatedPosition } from '~/constants/enum';
import useProductRelatedBySkus from '~/hooks/useProductRelatedBySkus';
import { Product } from '~/services/product';
import storeConfigAtom from '~/services/store-config/store-config.atom';
import ProductsSlider from '../../components/ProductsSlider';
import { useLazyQuery } from '@apollo/client';
import {
    QUERY_TRACK_VIEW_COLLECTION,
    QUERY_TRACK_VIEW_ITEM_RELATED,
    QUERY_TRACKING_IMPRESSION,
    QUERY_TRACKING_VIEW_RELATED
} from '~/data/product/gql';

interface BestSellerProps {
    title?: string;
    directLabel?: string;
    directUrl?: string;
    loading?: boolean;
    numberOfItems?: number;
    numberOfRows?: number;
    data?: Product[];
}

const BestSeller: FC<BestSellerProps> = ({
    title = 'BEST SELLER',
    directLabel = '',
    directUrl = '',
    numberOfItems = 4,
    numberOfRows = 1,
    data = []
}) => {
    const { best_seller_group_id } = useRecoilValue(storeConfigAtom?.store);
    const [startFetching, setStartFetching] = useState(false);
    const areRef = useRef<HTMLDivElement>(null);

    const [queryTrackViewItemRelated] = useLazyQuery(QUERY_TRACK_VIEW_ITEM_RELATED);

    const [queryTrackingViewList] = useLazyQuery(QUERY_TRACKING_VIEW_RELATED);
    const [queryTrackingViewCollection] = useLazyQuery(QUERY_TRACK_VIEW_COLLECTION);

    const [relatedTrackingImpression] = useLazyQuery(QUERY_TRACKING_IMPRESSION);

    const { products, loading, relatedProductsBySkus } = useProductRelatedBySkus({
        ruleId: best_seller_group_id,
        limit: 48,
        position: RelatedPosition.HomePage,
        skip: !startFetching
    });

    const handleStartFetching = (entries: IntersectionObserverEntry) => {
        setStartFetching(true);
    };

    const handleOnEnter = () => {
        console.log('queryTrackingViewList', best_seller_group_id);
        queryTrackingViewList({
            variables: {
                block_id: best_seller_group_id
            }
        });
        queryTrackingViewCollection({
            variables: {
                block_id: best_seller_group_id,
                type: 'related'
            }
        });
    };

    const handleImpression = (products: any[]) => {
        console.log('handleImpression', best_seller_group_id, products);
        queryTrackViewItemRelated({
            variables: {
                block_id: best_seller_group_id,
                skus: products?.map((prod) => prod?.sku)
            }
        });
        relatedTrackingImpression({
            variables: {
                skus: products?.map((prod) => prod?.sku),
                block_id: best_seller_group_id,
                type: 'related',
                search_query: ''
            }
        });
    };

    if (!products?.length && !loading && !!startFetching) {
        return null;
    }
    if (!best_seller_group_id) return null;

    return (
        <Container className="flex flex-col gap-2 lg:gap-2">
            <div ref={areRef} className="transform transition-all duration-500">
                <SectionTitle
                    text={relatedProductsBySkus?.block_title ?? title}
                    action={
                        directUrl ? (
                            <Link href={directUrl} className="flex items-center gap-1 text-primary">
                                <Text type="title-2">{directLabel}</Text>
                                <Icon name="arrow-up-right" />
                            </Link>
                        ) : undefined
                    }
                />

                <ProductsSlider
                    listkey={best_seller_group_id}
                    products={products}
                    hasNext={false}
                    // onLoadingMore={() => handleFetchMore(_PageInfo.current_page + 1)}
                    onBeforeIntersection={handleStartFetching}
                    onEnter={handleOnEnter}
                    onImpressions={handleImpression}
                    loading={loading}
                />
            </div>
        </Container>
    );
};

export default BestSeller;
